"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

interface PlotlyTableProps {
  trace: any;
  layout?: any;
  noBorder?: boolean;
}

function toStringSafe(v: any): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "";
  return String(v);
}

function stripHtmlTags(str: string): string {
  if (!str) return "";
  // Remove all HTML tags
  return str.replace(/<[^>]*>/g, "");
}

export function PlotlyTable({ trace, layout, noBorder = false }: PlotlyTableProps) {
  // Extract title from layout or trace
  const rawTitle = layout?.title?.text || layout?.title || trace?.name || null;
  const title = rawTitle ? stripHtmlTags(String(rawTitle)) : null;
  const headerValuesRaw = Array.isArray(trace?.header?.values)
    ? trace.header.values
    : [];

  let headers: string[] = [];
  if (headerValuesRaw.length > 0) {
    if (Array.isArray(headerValuesRaw[0])) {
      headers = (headerValuesRaw[0] as any[]).map((v) => toStringSafe(v));
    } else {
      headers = (headerValuesRaw as any[]).map((v) => toStringSafe(v));
    }
  }

  const cellsValues: any[][] = Array.isArray(trace?.cells?.values)
    ? (trace.cells.values as any[][])
    : [];

  const colCount = Math.max(headers.length, cellsValues.length);
  const rowCount = cellsValues.length
    ? Math.max(
        ...cellsValues.map((col) => (Array.isArray(col) ? col.length : 0))
      )
    : 0;

  const headerLabels = React.useMemo(
    () =>
      Array.from({ length: colCount }, (_, i) => headers[i] ?? `Column ${i + 1}`),
    [headers, colCount]
  );

  const colPercent = React.useMemo(() => (colCount > 0 ? 100 / colCount : 100), [colCount]);

  const tableMarkup = React.useMemo(() => {
    return (
      <div className="overflow-x-auto overflow-y-auto max-h-[450px] w-full">
        <table className="min-w-full w-[max-content] caption-bottom text-sm table-auto">
          <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm shadow-sm">
            <tr className="border-b border-border">
              {headerLabels.map((h, idx) => (
                <th 
                  key={`h-${idx}`}
                  className="px-2 py-1.5 text-left align-middle font-semibold text-xs text-foreground border-b-2 border-border whitespace-nowrap min-w-[100px]"
                  style={{ width: `${colPercent}%` }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowCount > 0 ? (
              Array.from({ length: rowCount }, (_, r) => (
                <tr 
                  key={`r-${r}`}
                  className="border-b border-border transition-colors hover:bg-muted/50"
                >
                  {Array.from({ length: colCount }, (_, c) => (
                    <td 
                      key={`c-${r}-${c}`}
                      className="px-2 py-1 align-middle text-xs whitespace-nowrap"
                      style={{ width: `${colPercent}%` }}
                    >
                      {toStringSafe(
                        Array.isArray(cellsValues[c]) ? cellsValues[c][r] : undefined
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={colCount} className="h-16 text-center text-xs">
                  No rows.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }, [headerLabels, rowCount, colCount, cellsValues]);

  return (
    <div
      className={
        noBorder
          ? "w-full max-w-full flex flex-col"
          : "w-full max-w-full flex flex-col rounded-lg overflow-hidden bg-card border border-border"
      }
    >
      {title && (
        <div className="px-3 py-2 border-b border-border bg-muted/30">
          <h3 className="text-sm font-semibold text-foreground text-center">{title}</h3>
        </div>
      )}
      <div className="min-h-0">{tableMarkup}</div>
    </div>
  );
}
